import React, { useLayoutEffect, useRef, useState } from 'react';

type HeaderCalloutProps = {
  children: React.ReactNode;
  anchorRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
};

const MAIN_COLUMN_SELECTOR = '[data-main-column]';
const TOAST_MAX_WIDTH = 400;

export const HeaderCallout = ({
  anchorRef,
  isOpen,
  children,
}: HeaderCalloutProps) => {
  const toastRef = useRef<HTMLDivElement>(null);

  const [showAbove, setShowAbove] = useState(false);
  const [arrowPos, setArrowPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [bounds, setBounds] = useState<{
    minLeft: number;
    maxLeft: number;
    width: number;
  }>({
    minLeft: 8,
    maxLeft: typeof window !== 'undefined' ? window.innerWidth - 16 : 8,
    width:
      typeof window !== 'undefined'
        ? Math.min(TOAST_MAX_WIDTH, window.innerWidth - 16)
        : TOAST_MAX_WIDTH,
  });

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return;

    const computeBounds = () => {
      const TOAST_WIDTH = Math.min(TOAST_MAX_WIDTH, window.innerWidth - 16);
      const columnEl = document.querySelector(
        MAIN_COLUMN_SELECTOR,
      ) as HTMLElement | null;

      if (columnEl) {
        const rect = columnEl.getBoundingClientRect();
        setBounds({
          minLeft: rect.left + 8,
          maxLeft: rect.right - TOAST_WIDTH - 8,
          width: TOAST_WIDTH,
        });
      } else {
        setBounds({
          minLeft: 8,
          maxLeft: window.innerWidth - TOAST_WIDTH - 8,
          width: TOAST_WIDTH,
        });
      }
    };

    const updatePosition = () => {
      if (!anchorRef.current) return;

      const rect = anchorRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const top = rect.bottom + 4;
      const left = rect.left + rect.width / 2;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      setArrowPos({ top, left });
      setShowAbove(spaceAbove > spaceBelow);

      computeBounds();
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        updatePosition();
      });
      ro.observe(document.body);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      ro?.disconnect();
    };
  }, [isOpen, anchorRef]);

  if (!isOpen || !arrowPos) return null;

  const verticalOffset = window.innerWidth < 768 ? 24 : 14;

  const toastLeft = (() => {
    const ideal = arrowPos.left - bounds.width / 2;
    return Math.max(bounds.minLeft, Math.min(ideal, bounds.maxLeft));
  })();

  const arrowLeftWithinToast = arrowPos.left - toastLeft - 8;

  return (
    <div
      ref={toastRef}
      className="fixed shadow-[4px_4px_6px_0px_rgba(0,_0,_0,_0.1)] md:leading-tight z-50"
      style={{
        top: showAbove
          ? arrowPos.top -
            verticalOffset -
            (toastRef.current?.offsetHeight || 0)
          : arrowPos.top + verticalOffset,
        left: toastLeft,
        width: `${bounds.width}px`,
        transition: 'top 0.15s ease-out',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute w-4 h-4 -z-10 bg-yellow-50 rotate-45"
        style={{
          top: showAbove ? 'auto' : -8,
          bottom: showAbove ? -8 : 'auto',
          left: `${Math.max(
            8,
            Math.min(
              arrowLeftWithinToast ?? bounds.width / 2 - 8,
              bounds.width - 16,
            ),
          )}px`,
        }}
      />
      <div className="bg-yellow-50 rounded-xl flex w-full items-start gap-2 md:gap-3 px-3 py-5 md:p-4 text-sm md:text-base font-medium">
        {children}
      </div>
    </div>
  );
};
